import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { EMPTY, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { AdminService } from "src/app/shared/services/admin/admin.service";
import {
  invitedTeachersListDelete,
  invitedTeachersListLoad,
  invitedTeachersListLoadFail,
  invitedTeachersListLoadSuccess,
  invitedTeachersListPost,
} from "../../actions/admin/invited-teachers.actions";
import { InvitedTeachers } from "src/app/shared/interfaces/orgadmin/invited-teacher";
import {
  sendInviteTeacher,
  sendInviteTeacherFail,
} from "../../actions/admin/send-invite-teacher.actions";
import {
  singleActiveTeacherLoad,
  singleActiveTeacherLoadSuccess,
  singleActiveTeacherLoadFail,
} from "../../actions/admin/single-active-teacher.actions";
import { TeacherDetails } from "src/app/shared/interfaces/orgadmin/course-teachers";
import {
  deleteInvitationTeacherLoad,
  deleteInvitationTeacherLoadFail,
  deleteInvitationTeacherLoadSuccess,
} from "../../actions/admin/delete-invitation-teacher.action";
import { createNotification } from "src/app/shared/utils/create-notification";
import { NotificationsService } from "src/app/shared/services/notifications/notifications.service";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { InvitedTeachersListState } from "../../reducers/admin/invited-teachers.reducer";

@Injectable()
export class InvitedTeachersEffect {
  getInivitedTeachersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invitedTeachersListLoad),
      switchMap((param) => {
        return this.adminService.getInvitedTeachers(param.id).pipe(
          map((res: InvitedTeachers) =>
            invitedTeachersListLoadSuccess({ invitedTeachers: res }),
          ),
          catchError(({ error }) => of(invitedTeachersListLoadFail(error))),
        );
      }),
    ),
  );

  sendInviteTeacherList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendInviteTeacher),
      switchMap((param) => {
        return this.adminService
          .sendInviteTeacher(param.organizationId, param.payload)
          .pipe(
            map((invitations) =>
              invitedTeachersListPost({
                invitations,
              }),
            ),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GLOBAL.SNACKBAR_MESSAGES.ORGADMIN_INVITE_TEACHER",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => {
              return of(sendInviteTeacherFail({ error }));
            }),
          );
      }),
    ),
  );

  getSingleActiveTeacher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(singleActiveTeacherLoad),
      switchMap((param) => {
        if (!param.organizationId) return EMPTY;
        return this.adminService
          .getActiveTeacher(param.organizationId, param.teacherId)
          .pipe(
            switchMap((res: TeacherDetails | null) => {
              if (!res) {
                return of(
                  singleActiveTeacherLoadSuccess({
                    activeTeacher: { user: null, subscriptions: [] },
                  }),
                );
              }
              return of(singleActiveTeacherLoadSuccess({ activeTeacher: res }));
            }),
            catchError((error: HttpErrorResponse) =>
              of(singleActiveTeacherLoadFail({ error })),
            ),
          );
      }),
    ),
  );

  deleteInvitationTeacher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteInvitationTeacherLoad),
      switchMap((param) => {
        return this.adminService
          .deleteInvitationTeacher(param.organizationId, param.invitationId)
          .pipe(
            tap((invitation) => {
              if (param.organizationId) {
                this.store.dispatch(
                  invitedTeachersListDelete({
                    invitation,
                  }),
                );
              }

              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GLOBAL.SNACKBAR_MESSAGES.ORGADMIN_REMOVE_INVITE_TEACHER",
                      {
                        email: param.email,
                      },
                    ),
                  },
                }),
              );
            }),
            map(() => deleteInvitationTeacherLoadSuccess()),
            catchError(({ error }) =>
              of(deleteInvitationTeacherLoadFail(error)),
            ),
          );
      }),
    ),
  );

  constructor(
    private adminService: AdminService,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
    private store: Store<{
      invitedTeachers: InvitedTeachersListState;
    }>,
  ) {}
}
